export type GridDirection = "up" | "down" | "left" | "right";

/** One rendered group's slice of the flat, navigable list. */
export interface GridGroupSpan {
  startIndex: number;
  count: number;
}

/**
 * Arrow-key movement across a grid that renders as several stacked grids —
 * one per group, each with its own header. A plain "index ± columns" is wrong
 * here the moment a group's last row is ragged: stepping down from it must
 * land in the NEXT group's first row, same column, not `columns` items later
 * in the flat list.
 *
 * Horizontal movement stays flat (crossing a group boundary walks into the
 * neighbouring group), vertical movement is column-preserving and clamps to
 * shorter rows. At the top and bottom edges the highlight stays put.
 */
export function navigateGroupedGrid(
  groups: GridGroupSpan[],
  columns: number,
  index: number,
  direction: GridDirection
): number {
  const totalCount = groups.reduce((sum, group) => sum + group.count, 0);
  if (totalCount === 0) return 0;

  if (direction === "left") return Math.max(index - 1, 0);
  if (direction === "right") return Math.min(index + 1, totalCount - 1);

  const groupIndex = groups.findIndex(
    (group) =>
      index >= group.startIndex && index < group.startIndex + group.count
  );
  if (groupIndex === -1) return index;

  const group = groups[groupIndex];
  const offset = index - group.startIndex;
  const row = Math.floor(offset / columns);
  const column = offset % columns;
  const lastRow = Math.floor((group.count - 1) / columns);

  if (direction === "down") {
    if (row < lastRow) {
      // The row below can be ragged — clamp to its last item.
      return group.startIndex + Math.min(offset + columns, group.count - 1);
    }

    const next = groups[groupIndex + 1];
    if (!next || next.count === 0) return index;
    return next.startIndex + Math.min(column, next.count - 1);
  }

  // up
  if (row > 0) {
    return group.startIndex + offset - columns;
  }

  const previous = groups[groupIndex - 1];
  if (!previous || previous.count === 0) return index;

  const previousLastRowStart =
    Math.floor((previous.count - 1) / columns) * columns;
  return (
    previous.startIndex +
    Math.min(previousLastRowStart + column, previous.count - 1)
  );
}
